import React, { useState, useEffect } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getToken, base_url } from "../../config/config";

// Helper function to convert hours to a formatted string (hours and minutes)
const convertHoursToTime = (hours) => {
  const totalHours = Math.floor(hours);
  const minutes = Math.round((hours - totalHours) * 60);
  return `${totalHours}h ${minutes}m`;
};

// Helper function to calculate cost based on hours
const calculateCost = (hours) => {
  return (hours * 60).toFixed(2); // Cost per hour is 60€, so multiply hours by 60
};

const AttendanceReport = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [hoursWorkedData, setHoursWorkedData] = useState([]);
  const [totalAttendanceHours, setTotalAttendanceHours] = useState(0);
  const [totalWorkedHours, setTotalWorkedHours] = useState(0);
  const [totalAttendanceCost, setTotalAttendanceCost] = useState(0);
  const [totalWorkedCost, setTotalWorkedCost] = useState(0);

  // New state to handle the order number and its total hours
  const [orderNumber, setOrderNumber] = useState("");
  const [orderHours, setOrderHours] = useState(0);
  const [orderCost, setOrderCost] = useState(0);

  useEffect(() => {
    // Fetch the attendance data from the original API
    const fetchAttendanceData = async () => {
      try {
        const response = await axios.get(`${base_url}/v1/AttendanceReport`);
        const data = response.data;
        setAttendanceData(data);

        // Calculate the total hours worked and cost in the attendance table
        const totalHours = data.reduce((sum, item) => sum + item.totalHours, 0);
        const totalCost = data.reduce(
          (sum, item) => sum + item.totalHours * 60,
          0,
        );
        setTotalAttendanceHours(totalHours);
        setTotalAttendanceCost(totalCost);
      } catch (error) {
        console.error("Error fetching the attendance data:", error);
      }
    };

    // Fetch the total hours worked data from the new API
    const fetchHoursWorkedData = async () => {
      try {
        const response = await axios.get(
          "http://35.159.43.224:3080/total-hours-by-user",
        );
        const data = response.data;
        setHoursWorkedData(data);

        // Calculate the total hours worked and cost in the new hours worked table
        const totalHours = data.reduce((sum, item) => sum + item.totalHours, 0);
        const totalCost = data.reduce(
          (sum, item) => sum + item.totalHours * 60,
          0,
        );
        setTotalWorkedHours(totalHours);
        setTotalWorkedCost(totalCost);
      } catch (error) {
        console.error("Error fetching the total hours worked data:", error);
      }
    };

    fetchAttendanceData();
    fetchHoursWorkedData();
  }, []);

  // Function to handle order number input and fetch hours
  const handleOrderNumberChange = (e) => {
    setOrderNumber(e.target.value);
  };

  const fetchOrderHours = async () => {
    try {
      const response = await axios.get(
        `http://35.159.43.224:3080/total-hours-for-orderReg/${orderNumber}`,
      );
      const data = response.data;
      setOrderHours(data.totalHours);
      setOrderCost(calculateCost(data.totalHours));
    } catch (error) {
      console.error("Error fetching total hours for order number:", error);
    }
  };

  // Function to generate and download PDF
  const handlePrintPDF = () => {
    const input = document.getElementById("pdf-content");
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      const imgWidth = 210; // A4 size width in mm
      const pageHeight = 295; // A4 size height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          0,
          heightLeft - imgHeight,
          imgWidth,
          imgHeight,
        );
        heightLeft -= pageHeight;
      }

      pdf.save("attendance-report.pdf");
    });
  };

  return (
    <div style={{ padding: "20px", fontFamily: "'Arial', sans-serif" }}>
      <button
        onClick={handlePrintPDF}
        style={{
          marginBottom: "20px",
          padding: "10px 20px",
          backgroundColor: "#007bff",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
        }}>
        Download PDF
      </button>

      {/* Input and button for order number */}
      <div style={{ marginBottom: "20px" }}>
        <input
          type='text'
          value={orderNumber}
          onChange={handleOrderNumberChange}
          placeholder='Enter Order Number'
          style={{
            padding: "10px",
            fontSize: "16px",
            borderRadius: "5px",
            border: "1px solid #ddd",
            width: "200px",
          }}
        />
        <button
          onClick={fetchOrderHours}
          style={{
            padding: "10px 20px",
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            marginLeft: "10px",
          }}>
          Fetch Hours
        </button>
      </div>

      <div
        id='pdf-content'
        style={{ display: "flex", justifyContent: "space-between" }}>
        {/* Original Attendance Table */}
        <div
          style={{
            width: "48%",
            backgroundColor: "#f9f9f9",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
          }}>
          <h2 style={{ textAlign: "center", color: "#333" }}>
            Employee Attendance Summary
          </h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#007BFF", color: "white" }}>
                <th style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  Employee
                </th>
                <th style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  Total Hours Worked
                </th>
                <th style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  Number of Absences
                </th>
                <th style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  Cost (€)
                </th>
              </tr>
            </thead>
            <tbody>
              {attendanceData.map((item) => (
                <tr key={item.employee}>
                  <td
                    style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    {item.employee}
                  </td>
                  <td
                    style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    {convertHoursToTime(item.totalHours)}
                  </td>
                  <td
                    style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    {item.absenceCount}
                  </td>
                  <td
                    style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    {calculateCost(item.totalHours)}€
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: "bold", backgroundColor: "#f1f1f1" }}>
                <td style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  Total
                </td>
                <td style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  {convertHoursToTime(totalAttendanceHours)}
                </td>
                <td
                  style={{
                    padding: "10px",
                    borderBottom: "2px solid #ddd",
                  }}></td>
                <td style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  {totalAttendanceCost.toFixed(2)}€
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* New Total Hours Worked Table */}
        <div
          style={{
            width: "48%",
            backgroundColor: "#f9f9f9",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
          }}>
          <h2 style={{ textAlign: "center", color: "#333" }}>
            Total Hours Worked by User
          </h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#28a745", color: "white" }}>
                <th style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  User
                </th>
                <th style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  Total Hours Worked
                </th>
                <th style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  Cost (€)
                </th>
              </tr>
            </thead>
            <tbody>
              {hoursWorkedData.map((item) => (
                <tr key={item.user}>
                  <td
                    style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    {item.user}
                  </td>
                  <td
                    style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    {convertHoursToTime(item.totalHours)}
                  </td>
                  <td
                    style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    {calculateCost(item.totalHours)}€
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: "bold", backgroundColor: "#f1f1f1" }}>
                <td style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  Total
                </td>
                <td style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  {convertHoursToTime(totalWorkedHours)}
                </td>
                <td style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>
                  {totalWorkedCost.toFixed(2)}€
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Display fetched order total hours and cost */}
      {orderNumber && (
        <div
          style={{
            marginTop: "20px",
            backgroundColor: "#f9f9f9",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
          }}>
          <h3>Total Hours and Cost for Order Number {orderNumber}</h3>
          <p>
            <strong>Total Hours:</strong> {convertHoursToTime(orderHours)}
          </p>
          <p>
            <strong>Total Cost (€):</strong> {orderCost}€
          </p>
        </div>
      )}
    </div>
  );
};

export default AttendanceReport;
